import { router } from "expo-router";
import { observer } from "mobx-react-lite";
import React, { useEffect } from "react";
import { Image, Platform, StyleSheet, useWindowDimensions } from "react-native";
import RNPermissions from "react-native-permissions";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated";
import { Button } from "~/components/Button";
import { Note } from "~/components/Note";
import { Screen } from "~/components/Screen";
import { Spacer } from "~/components/Spacer";
import { Text } from "~/components/Text";
import { View } from "~/components/View";
import { FormattedMessage, getLocaleURL } from "~/features/intl";
import { useAlert } from "~/hooks/useAlert";
import { styleConstants } from "~/style/styleConstants";
import { delay } from "~/utils/delay";
import { promptYesNo } from "~/utils/promptYesNo";
import { useToast } from "../toast";
import { RulesModal } from "./RulesModal";

export const LiveVideoIntroScreen = observer(function LiveVideoIntroScreen() {
  const toast = useToast();
  const alert = useAlert();
  const window = useWindowDimensions();

  const [isRulesModalVisible, setIsRulesModalVisible] = React.useState(false);

  const spaceBetweenRings = 28;
  const outerRingSize = Math.min(
    window.width - styleConstants.spacingLarge * 4,
    window.height * 0.5,
  );

  const animatedDegreesOuter = useSharedValue(0);
  useEffect(() => {
    animatedDegreesOuter.value = withRepeat(
      withTiming(Math.PI * 2, { duration: 60000, easing: Easing.linear }),
      -1,
      false,
    );
  }, [animatedDegreesOuter]);
  const animatedDegreesInner = useSharedValue(0);
  useEffect(() => {
    animatedDegreesInner.value = withRepeat(
      withTiming(Math.PI * 2, { duration: 50000, easing: Easing.linear }),
      -1,
      false,
    );
  }, [animatedDegreesInner]);

  const rotate0deg = useAnimatedStyle(() => {
    return {
      transform: [
        // Add half of the image size to the translateX and translateY to center the image
        { translateX: outerRingSize / 2 - 16 },
        { translateY: outerRingSize / 2 - 16 },
        {
          translateX:
            (outerRingSize / 2) * Math.cos(0 + animatedDegreesOuter.value),
        },
        {
          translateY:
            (outerRingSize / 2) * Math.sin(0 + animatedDegreesOuter.value),
        },
      ],
    };
  });
  const rotate120deg = useAnimatedStyle(() => {
    return {
      transform: [
        // Add half of the image size to the translateX and translateY to center the image
        { translateX: outerRingSize / 2 - 16 },
        { translateY: outerRingSize / 2 - 16 },
        {
          translateX:
            (outerRingSize / 2) *
            Math.cos(((Math.PI * 2) / 6) * 2 + animatedDegreesOuter.value),
        },
        {
          translateY:
            (outerRingSize / 2) *
            Math.sin(((Math.PI * 2) / 6) * 2 + animatedDegreesOuter.value),
        },
      ],
    };
  });
  const rotate240deg = useAnimatedStyle(() => {
    return {
      transform: [
        // Add half of the image size to the translateX and translateY to center the image
        { translateX: outerRingSize / 2 - 16 },
        { translateY: outerRingSize / 2 - 16 },
        {
          translateX:
            (outerRingSize / 2) *
            Math.cos(((Math.PI * 2) / 6) * 4 + animatedDegreesOuter.value),
        },
        {
          translateY:
            (outerRingSize / 2) *
            Math.sin(((Math.PI * 2) / 6) * 4 + animatedDegreesOuter.value),
        },
      ],
    };
  });
  // Rotations for small images
  const rotate60deg = useAnimatedStyle(() => {
    return {
      transform: [
        // Add half of the image size to the translateX and translateY to center the image
        { translateX: outerRingSize / 2 - 8 },
        { translateY: outerRingSize / 2 - 8 },
        {
          translateX:
            ((outerRingSize - 2 * spaceBetweenRings) / 2) *
            Math.cos(((Math.PI * 2) / 6) * 1 + animatedDegreesInner.value),
        },
        {
          translateY:
            ((outerRingSize - 2 * spaceBetweenRings) / 2) *
            Math.sin(((Math.PI * 2) / 6) * 1 + animatedDegreesInner.value),
        },
      ],
    };
  });
  const rotate180deg = useAnimatedStyle(() => {
    return {
      transform: [
        // Add half of the image size to the translateX and translateY to center the image
        { translateX: outerRingSize / 2 - 8 },
        { translateY: outerRingSize / 2 - 8 },
        {
          translateX:
            ((outerRingSize - 2 * spaceBetweenRings) / 2) *
            Math.cos(((Math.PI * 2) / 6) * 3 + animatedDegreesInner.value),
        },
        {
          translateY:
            ((outerRingSize - 2 * spaceBetweenRings) / 2) *
            Math.sin(((Math.PI * 2) / 6) * 3 + animatedDegreesInner.value),
        },
      ],
    };
  });
  const rotate300deg = useAnimatedStyle(() => {
    return {
      transform: [
        // Add half of the image size to the translateX and translateY to center the image
        { translateX: outerRingSize / 2 - 8 },
        { translateY: outerRingSize / 2 - 8 },
        {
          translateX:
            ((outerRingSize - 2 * spaceBetweenRings) / 2) *
            Math.cos(((Math.PI * 2) / 6) * 5 + animatedDegreesInner.value),
        },
        {
          translateY:
            ((outerRingSize - 2 * spaceBetweenRings) / 2) *
            Math.sin(((Math.PI * 2) / 6) * 5 + animatedDegreesInner.value),
        },
      ],
    };
  });

  // Use this function to create the style for the rings
  // and also the center image in the middle of the rings
  const createRingStyleWithDiameter = (
    diameter: number,
    left: number,
    top: number,
  ) => {
    return {
      position: "absolute" as const,
      top,
      left,
      width: diameter,
      height: diameter,
      borderRadius: diameter / 2,
      borderWidth: 1,
      borderColor: styleConstants.colorBackgroundLightDark,
    };
  };

  return (
    <Screen withTopInset withBottomInset={false} preventScroll>
      <View flex paddingLarge paddingVerticalExtraLarge>
        {/* Animated avatars */}
        <View paddingVerticalLarge justifyContentCenter flex>
          <View
            style={{
              alignSelf: "center",
              width: outerRingSize,
              height: outerRingSize,
            }}
          >
            {/* Ring 1 */}
            <View style={createRingStyleWithDiameter(outerRingSize, 0, 0)} />

            {/* Ring 2 */}
            <View
              style={createRingStyleWithDiameter(
                outerRingSize - spaceBetweenRings * 2,
                spaceBetweenRings,
                spaceBetweenRings,
              )}
            />

            {/* Center round image */}
            <Image
              source={require("~/assets/images/video-intro/blind-date.jpg")}
              style={[
                createRingStyleWithDiameter(
                  outerRingSize - spaceBetweenRings * 4,
                  spaceBetweenRings * 2,
                  spaceBetweenRings * 2,
                ),
              ]}
              resizeMode="cover"
            />

            {/* Avatar poistioned at 0 degrees */}
            <Animated.Image
              source={require("~/assets/images/video-intro/blind-date-avatar-1.jpg")}
              style={[S.avatarLarge, rotate0deg]}
              resizeMode="cover"
            />
            {/* Avatar poistioned at 120 degrees */}
            <Animated.Image
              source={require("~/assets/images/video-intro/blind-date-avatar-2.jpg")}
              style={[S.avatarLarge, rotate120deg]}
              resizeMode="cover"
            />
            {/* Avatar poistioned at 240 degrees */}
            <Animated.Image
              source={require("~/assets/images/video-intro/blind-date-avatar-3.jpg")}
              style={[S.avatarLarge, rotate240deg]}
              resizeMode="cover"
            />

            {/* Small avatar poistioned at 60 degrees */}
            <Animated.Image
              source={require("~/assets/images/video-intro/blind-date-avatar-small-1.jpg")}
              style={[S.avatarSmall, rotate60deg]}
              resizeMode="cover"
            />
            {/* Small avatar poistioned at 180 degrees */}
            <Animated.Image
              source={require("~/assets/images/video-intro/blind-date-avatar-small-2.jpg")}
              style={[S.avatarSmall, rotate180deg]}
              resizeMode="cover"
            />
            {/* Small avatar poistioned at 300 degrees */}
            <Animated.Image
              source={require("~/assets/images/video-intro/blind-date-avatar-small-3.jpg")}
              style={[S.avatarSmall, rotate300deg]}
              resizeMode="cover"
            />
          </View>
        </View>

        <Spacer extraLarge />

        <Text sizeExtraLarge weightExtraBold alignCenter>
          <FormattedMessage
            defaultMessage="1 on 1 video date"
            id="ExCgPV"
            description="Title for the LiveVideoIntroScreen. This is the screen where user is introduced to the blind date feature. Has to be short"
          />
        </Text>

        <Spacer medium />

        <Text sizeMedium alignCenter>
          <FormattedMessage
            defaultMessage="Connect live and privately with someone new at the tap of a button. Start chatting and explore where it leads. Simple and exciting!"
            id="qkZ3u4"
            description="Description for the LiveVideoIntroScreen. This is the screen where user is introduced to the blind date feature"
          />
        </Text>

        <Spacer extraLarge />

        <Note>
          <Text sizeSmall>
            {intl.formatMessage({
              defaultMessage:
                "Please be kind and respectful, you're chatting with a real person on the other side",
              id: "yfvmTG",
              description:
                "Note on LiveVideoIntroScreen. This is the screen where user is introduced to the date feature. This note is shown to remind users to be kind and respectful",
            })}
          </Text>
        </Note>

        <Spacer large />

        <Button
          style={{ minWidth: 250, alignSelf: "center" }}
          large
          title={intl.formatMessage({
            defaultMessage: "Let's go!",
            id: "dJXjNH",
            description:
              "Button title for the LiveVideoIntroScreen. This is the screen where user is introduced to the blind date feature.",
          })}
          onPress={() => {
            setIsRulesModalVisible(true);
          }}
        />

        <Spacer medium />
        {Platform.OS !== "web" && (
          <Button
            style={{ minWidth: 250, alignSelf: "center" }}
            transparent
            onPress={async function onNotifyMePressed() {
              toast.showToast({
                message: "Not implemented yet",
                type: "error",
              });
              // This is to prevent the user from accidentally tapping the button multiple times
              await delay(100);
            }}
          >
            <Text underline colorTheme weightSemiBold>
              {intl.formatMessage({
                defaultMessage: "See events",
                id: "9h08m2",
                description: "View events button on LiveVideoChatScreen",
              })}
            </Text>
          </Button>
        )}
      </View>

      {isRulesModalVisible && (
        <RulesModal
          onAccept={async () => {
            setIsRulesModalVisible(false);

            if (Platform.OS === "android") {
              const result = await RNPermissions.check(
                RNPermissions.PERMISSIONS.ANDROID.BLUETOOTH_CONNECT,
              );
              if (result !== "granted") {
                const shouldRequest = await promptYesNo(
                  {
                    title: intl.formatMessage({
                      id: "jIyOd1",
                      defaultMessage: "Grant Bluetooth permission?",
                      description:
                        "Title for the alert that asks user to grant Bluetooth permission. This is shown when user is about to start a live video chat. Word Bluetooth should be the same in all languages as it is universally known",
                    }),
                    message: intl.formatMessage({
                      id: "OQIqyq",
                      defaultMessage:
                        "Enable Bluetooth permission to seamlessly switch audio to your headset when connected. It's optional but ensures a hassle-free audio transition",
                      description:
                        "Message for the alert that asks user to grant Bluetooth permission. This is shown when user is about to start a live video chat. Word Bluetooth should be the same in all languages as it is universally known",
                    }),
                    yesText: intl.formatMessage({
                      id: "Wo8Kj4",
                      defaultMessage: "Enable",
                      description:
                        "Text for the button that enables Bluetooth permission. This is shown when user is about to start a live video chat",
                    }),
                    noText: intl.formatMessage({
                      id: "JM99zS",
                      defaultMessage: "Not now",
                      description:
                        "Text for the button that does not enable Bluetooth permission.",
                    }),
                  },
                  alert,
                );

                if (shouldRequest) {
                  await RNPermissions.request(
                    RNPermissions.PERMISSIONS.ANDROID.BLUETOOTH_CONNECT,
                  );
                }
              }
            }

            router.navigate(getLocaleURL("/video-settings"));
          }}
          onCancel={() => {
            setIsRulesModalVisible(false);
          }}
        />
      )}
    </Screen>
  );
});

const S = StyleSheet.create({
  avatarLarge: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  avatarSmall: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 16,
    height: 16,
    borderRadius: 8,
  },
});
